// ─── The shape of the run on the ground ──────────────────────────────────────
//
// Google's driving route between the container's located stops, leg by leg, so
// the map can draw the line the truck actually takes and label each hop with
// its own distance. Falls back to straight lines between the stops when
// Directions cannot answer — an unenabled key, a quota, a pair of points with
// no road between them — because a route map that disappears on the day the
// billing changes is worse than one that says it is estimating.
//
// Answers are cached by route rather than by container: containers generated
// from one order share a route, so a dispatcher opening six of them off the
// board asks Google once. The cache is capped — a long session on a big board
// would otherwise hold every route the dispatcher ever glanced at.

import { haversineKm, routeMapFingerprint } from "../../utils/routeMapPoints";
import { loadGoogleMapsLibrary } from "../works/useGooglePlacesSuggestions";
import type { RoutePoint } from "../../utils/routeMapPoints";

export interface RouteLeg {
  /**
   * The line to draw for this hop, in order.
   *
   * Google hands back `LatLng` objects and the straight-line fallback builds
   * literals; polylines and bounds take either, so neither is converted.
   */
  path: Array<google.maps.LatLng | google.maps.LatLngLiteral>;
  km: number;
}

export interface RouteShape {
  legs: RouteLeg[];
  /** `road` is Google's driving line; `straight` is the estimate. */
  source: "road" | "straight";
  /**
   * The whole run, in km — summed from the legs *before* any of them is
   * rounded for display.
   *
   * Carried rather than left for the caller to add up, because adding up the
   * rounded chips is not the same sum: six legs shown to one decimal can be
   * half a kilometre away from the distance actually driven.
   */
  totalKm: number;
}

/** Google takes 25 intermediates. A container run never comes close. */
const MAX_INTERMEDIATES = 25;

const CACHE_LIMIT = 40;
const cache = new Map<string, RouteShape>();
const inFlight = new Map<string, Promise<RouteShape>>();
let warned = false;

const remember = (key: string, shape: RouteShape) => {
  // Oldest out first: insertion order is what a Map iterates in.
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, shape);
};

const plain = (stop: RoutePoint) => ({ lat: stop.lat, lng: stop.lng });

/** Stop to stop in a straight line — the estimate, and the fallback. */
const straightShape = (stops: RoutePoint[]): RouteShape => {
  const legs = stops.slice(1).map((stop, i) => ({
    path: [plain(stops[i]), plain(stop)],
    km: haversineKm(stops[i], stop),
  }));

  return { source: "straight", legs, totalKm: sumKm(legs) };
};

/** The run, added up before anything is rounded. */
const sumKm = (legs: RouteLeg[]) => legs.reduce((km, leg) => km + leg.km, 0);

/**
 * The driving route over these stops, in one Directions request.
 *
 * The leg paths are taken from the steps rather than `overview_path`: the
 * overview is simplified for the whole journey, and the map needs to know where
 * one leg ends and the next begins to put a distance label on each.
 */
async function roadShape(stops: RoutePoint[]): Promise<RouteShape> {
  // Loaded here, not assumed. A map on the page has already done this, but the
  // truck report measures without drawing anything — and reaching straight for
  // `window.google` there threw, fell back to straight lines, and produced a
  // figure identical to not having measured at all. `importLibrary` is cached,
  // so where a map did load this first, asking again costs nothing.
  await Promise.all(["core", "routes"].map(loadGoogleMapsLibrary));

  const maps: typeof google.maps = window.google.maps;
  const service = new maps.DirectionsService();

  const result = await service.route({
    origin: plain(stops[0]),
    destination: plain(stops[stops.length - 1]),
    waypoints: stops
      .slice(1, -1)
      .slice(0, MAX_INTERMEDIATES)
      .map((stop) => ({ location: plain(stop), stopover: true })),
    travelMode: maps.TravelMode.DRIVING,
    // The order of a container's stops is the plan, not a suggestion.
    optimizeWaypoints: false,
  });

  const route = result?.routes?.[0];
  if (!route?.legs?.length) throw new Error("Directions returned no route");

  const legs = route.legs.map((leg) => ({
    path: (leg.steps ?? []).flatMap((step) => step.path ?? []),
    km: (leg.distance?.value ?? 0) / 1000,
  }));

  return { source: "road", legs, totalKm: sumKm(legs) };
}

/**
 * How to draw this run — cached, and never rejecting: a failed lookup comes
 * back as the straight-line estimate, which the map then labels as one.
 */
export function routeShape(stops: RoutePoint[]): Promise<RouteShape> {
  const key = routeMapFingerprint(stops);

  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);

  const running = inFlight.get(key);
  if (running) return running;

  const job = (async (): Promise<RouteShape> => {
    if (stops.length < 2) return { legs: [], source: "road", totalKm: 0 };
    try {
      return await roadShape(stops);
    } catch (error) {
      // Once per session is enough: a board of forty would otherwise log
      // forty identical lines for one disabled API.
      if (!warned) {
        warned = true;
        console.warn(
          "Route map: falling back to straight lines —",
          (error as Error)?.message,
        );
      }
      return straightShape(stops);
    }
  })();

  const tracked = job.then((shape) => {
    // Only a real answer is remembered. A straight-line shape here is what a
    // *failed* lookup fell back to — Maps not loaded yet, a refused key, a
    // quota — and caching that would make the failure permanent for the rest
    // of the session, long after the cause had gone. The server reasons the
    // same way about its own fallback, and for the same reason.
    if (shape.source === "road") remember(key, shape);
    inFlight.delete(key);
    return shape;
  });

  inFlight.set(key, tracked);
  return tracked;
}
